import { Draft, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { constructorExercises } from "constants/exercises";
import { TUrlParam } from "types/utils";
import { EExerciseType, TExerciseItem } from "types/app/exercises";
import { TExercise, TExerciseTarget } from "types/api/exercise";
import { EAppReducers } from "../../white-list";
import { TTestExercise } from "types/app/exercises/test-exercise";

export type TExerciseState<
  TExercisePayload extends Record<string, unknown> = Record<string, unknown>
> = {
  /**
   * Field for ID of the course. Required for exercise manipulation with API. Should be provided before any exercise API event, otherwise request wont be executed.
   * @default null
   */
  courseId: TUrlParam;
  /**
   * Field for ID of the course lesson. Required for exercise manipulation with API. Should be provided before any exercise API event, otherwise request wont be executed.
   * @default null
   */
  lessonId: TUrlParam;
  /**
   * Field for ID of the lesson chapter. Required for exercise manipulation with API. Should be provided before any exercise API event, otherwise request wont be executed.
   * @default null
   */
  chapterId: TUrlParam;
  /**
   * Field for ID of the lesson homework. Required for exercise manipulation with API. Should be provided before any exercise API event, otherwise request wont be executed.
   * @default null
   */
  homeworkId: TUrlParam;

  /**
   * List of all exercises.
   */
  exercises: any[];
};

/**
 * This function will create slices for exercise constructor. Main reason is to make generic exercise payload slices.
 */
const createExerciseSlices = <
  TExercisePayload extends Record<string, unknown> = Record<string, unknown>
>() => {
  const initialState: TExerciseState<TExercisePayload> = {
    courseId: null,
    lessonId: null,
    chapterId: null,
    homeworkId: null,
    exercises: [],
  };

  return createSlice({
    name: EAppReducers.EXERCISE,
    initialState,
    reducers: {
      setExerciseState: (state, action: PayloadAction<any[]>) => {
        state.exercises = action.payload;
      },
      updateExercise: (state, action: PayloadAction<any>) => {
        state.exercises = state.exercises.map((exercise) =>
          exercise.id === action.payload.id ? action.payload : exercise
        );
      },
      resetExercises: (state) => {
        state.exercises = initialState.exercises;
      },
      setCourseId: (state, action: PayloadAction<TUrlParam>) => {
        state.courseId = action.payload;
      },
      setLessonId: (state, action: PayloadAction<TUrlParam>) => {
        state.lessonId = action.payload;
      },
      setChapterId: (state, action: PayloadAction<TUrlParam>) => {
        state.chapterId = action.payload;
      },
      setHomeworkId: (state, action: PayloadAction<TUrlParam>) => {
        state.homeworkId = action.payload;
      },
    },
  });
};

export const exerciseSlices = createExerciseSlices();
export const { setExerciseState, updateExercise } = exerciseSlices.actions;
export const exerciseReducer = exerciseSlices.reducer;
